import { useShopwareContext, useLocalePath, useInternationalization } from '#imports'
import { useRoute } from 'vue-router'
import type { LocationQuery } from 'vue-router'
import type { operations } from '#shopware'

/**
 * Extra fields spread into the `/handle-payment` body beyond `orderId`/`finishUrl`/`errorUrl`
 * (which `handlePayment` builds itself). Either the raw route query (redirect flow) or the typed
 * Mollie payment-body fields — notably `savePaymentDetails: boolean` — so they typecheck
 * (ADR-0006, see storeApiTypes overrides). A union, not an intersection: `LocationQuery`'s
 * string-only index signature is incompatible with a boolean field.
 */
type HandlePaymentDataBag =
    | LocationQuery
    | Partial<Omit<operations['handlePaymentMethod post /handle-payment']['body'], 'orderId' | 'finishUrl' | 'errorUrl'>>

export function usePayment() {
    const { apiClient } = useShopwareContext()
    const localePath = useLocalePath()
    const { formatLink, getStorefrontUrl } = useInternationalization(localePath)
    const route = useRoute()
    const runtimeConfig = useRuntimeConfig()

    async function handlePayment(orderId: string, dataBag?: HandlePaymentDataBag) {
        const basePath = (runtimeConfig.app?.baseURL || '/') === '/' ? '' : (runtimeConfig.app?.baseURL || '')
        const finishUrl = `${getStorefrontUrl()}${basePath}${formatLink(`/checkout/success?orderId=${orderId}`)}`
        const errorUrl = `${getStorefrontUrl()}${basePath}${formatLink(`/checkout/error?orderId=${orderId}`)}`
        
        if (dataBag === null) {
            dataBag = route.query
        }

        const response = await apiClient.invoke('handlePaymentMethod post /handle-payment', {
            body: {
                orderId,
                finishUrl,
                errorUrl,
                ...(dataBag != null && { ...dataBag })
            },
        })

        return {
            redirectUrl: response.data.redirectUrl,
            isSync: !response.data.redirectUrl,
        }
    }

    return {
        handlePayment,
    }
}
